import { PersonalShopperOrder, StoreProduct, YahooAuctionBid, PackageItem } from '../types';

export const PAYMENT_DEADLINE_DAYS = 5;
export const ARCHIVE_THRESHOLD_DAYS = 14;

export const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const PAYMENT_DEADLINE_MS = PAYMENT_DEADLINE_DAYS * MS_PER_DAY; // 5 days = 432,000,000 ms
export const ARCHIVE_THRESHOLD_MS = ARCHIVE_THRESHOLD_DAYS * MS_PER_DAY; // 14 days = 1,209,600,000 ms

export interface PaymentDeadlineInfo {
  isExpired: boolean;
  deadlineDate: Date;
  daysRemaining: number;
  hoursRemaining: number;
  minutesRemaining: number;
  formattedTimeLeft: string;
  formattedDeadlineDate: string;
  percentRemaining: number; // 0 to 100
  urgency: 'normal' | 'warning' | 'critical' | 'expired';
}

/**
 * Calculates payment deadline info based on a 5-day expiration limit.
 */
export function getPaymentDeadlineInfo(dateStr?: string, daysLimit: number = PAYMENT_DEADLINE_DAYS): PaymentDeadlineInfo {
  if (!dateStr) {
    const now = new Date();
    return {
      isExpired: false,
      deadlineDate: now,
      daysRemaining: daysLimit,
      hoursRemaining: daysLimit * 24,
      minutesRemaining: daysLimit * 24 * 60,
      formattedTimeLeft: `${daysLimit} dias restantes`,
      formattedDeadlineDate: now.toLocaleDateString('pt-BR'),
      percentRemaining: 100,
      urgency: 'normal'
    };
  }

  const creationTime = new Date(dateStr).getTime();
  const deadlineTime = creationTime + daysLimit * MS_PER_DAY;
  const deadlineDate = new Date(deadlineTime);
  const now = Date.now();
  const timeDiff = deadlineTime - now;

  if (timeDiff <= 0) {
    return {
      isExpired: true,
      deadlineDate,
      daysRemaining: 0,
      hoursRemaining: 0,
      minutesRemaining: 0,
      formattedTimeLeft: 'Prazo expirado',
      formattedDeadlineDate: deadlineDate.toLocaleDateString('pt-BR'),
      percentRemaining: 0,
      urgency: 'expired'
    };
  }

  const daysRemaining = Math.floor(timeDiff / MS_PER_DAY);
  const hoursRemaining = Math.floor((timeDiff % MS_PER_DAY) / (60 * 60 * 1000));
  const minutesRemaining = Math.floor((timeDiff % (60 * 60 * 1000)) / (60 * 1000));

  let formattedTimeLeft = '';
  let urgency: 'normal' | 'warning' | 'critical' | 'expired' = 'normal';

  if (daysRemaining > 1) {
    formattedTimeLeft = `${daysRemaining} dias restantes`;
    urgency = 'normal';
  } else if (daysRemaining === 1) {
    formattedTimeLeft = `1 dia e ${hoursRemaining}h restantes`;
    urgency = 'warning';
  } else if (hoursRemaining > 0) {
    formattedTimeLeft = `${hoursRemaining}h e ${minutesRemaining}min restantes`;
    urgency = 'critical';
  } else {
    formattedTimeLeft = `${minutesRemaining} min restantes`;
    urgency = 'critical';
  }

  const totalDuration = daysLimit * MS_PER_DAY;
  const percentRemaining = Math.max(0, Math.min(100, Math.round((timeDiff / totalDuration) * 100)));

  return {
    isExpired: false,
    deadlineDate,
    daysRemaining,
    hoursRemaining,
    minutesRemaining,
    formattedTimeLeft,
    formattedDeadlineDate: deadlineDate.toLocaleDateString('pt-BR') + ' às ' + deadlineDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    percentRemaining,
    urgency
  };
}

/**
 * Checks if an order or record is older than 14 days (should leave the active visual list).
 */
export function isOrderOlderThan14Days(dateStr?: string): boolean {
  if (!dateStr) return false;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return false;
  return Date.now() - t > ARCHIVE_THRESHOLD_MS;
}

/**
 * Checks if an order should be shown in the active operational visual list:
 * - Must be less than 14 days old, OR
 * - Currently active/in-progress (e.g. 'Pago - Em Compra', 'Comprado' awaiting warehouse arrival).
 */
export function isOrderActiveVisual(order: { createdAt?: string; updatedAt?: string; status?: string }): boolean {
  const referenceDate = order.createdAt || order.updatedAt;
  const older = isOrderOlderThan14Days(referenceDate);
  
  if (!older) return true;

  // If older than 14 days, only keep if actively being processed right now
  const norm = (order.status || '').toLowerCase().trim();
  if (norm.includes('em compra') || norm.includes('retido') || norm.includes('arrematado')) {
    return true; // Still in transit / being processed by admin in Japan
  }

  return false; // Out of the visual list to keep interface clean!
}

/**
 * Automatically inspects Personal Shopper orders and cancels those where 5-day payment limit has expired.
 */
export function autoCancelExpiredPersonalShopperOrders(
  orders: PersonalShopperOrder[]
): {
  updatedOrders: PersonalShopperOrder[];
  hasChanges: boolean;
  cancelledOrders: PersonalShopperOrder[];
} {
  let hasChanges = false;
  const cancelledOrders: PersonalShopperOrder[] = [];

  const updatedOrders = orders.map((order) => {
    // Only orders awaiting payment are subject to 5-day cancellation
    if (order.status === 'Aguardando Pagamento') {
      const deadline = getPaymentDeadlineInfo(order.createdAt, PAYMENT_DEADLINE_DAYS);
      if (deadline.isExpired) {
        hasChanges = true;
        const cancelled: PersonalShopperOrder = {
          ...order,
          status: 'Cancelado',
          notes: (order.notes ? `${order.notes} | ` : '') + 'Cancelado automaticamente por falta de pagamento (prazo de 5 dias expirado).',
          updatedAt: new Date().toISOString()
        };
        cancelledOrders.push(cancelled);
        return cancelled;
      }
    }
    return order;
  });

  return { updatedOrders, hasChanges, cancelledOrders };
}

/**
 * Automatically inspects Store Products and releases reservations where 5-day payment limit has expired.
 */
export function autoCancelExpiredStoreReservations(
  products: StoreProduct[]
): {
  updatedProducts: StoreProduct[];
  hasChanges: boolean;
  cancelledReservations: Array<{ product: StoreProduct; reservation: any }>;
} {
  let hasChanges = false;
  const cancelledReservations: Array<{ product: StoreProduct; reservation: any }> = [];

  const updatedProducts = products.map((product) => {
    const isReserved = product.isReserved || product.reservationStatus === 'reservado';
    const reserveDate = product.reservation?.reservedAt || product.reservedAt;

    if (isReserved && reserveDate) {
      const deadline = getPaymentDeadlineInfo(reserveDate, PAYMENT_DEADLINE_DAYS);
      if (deadline.isExpired) {
        hasChanges = true;
        cancelledReservations.push({
          product,
          reservation: product.reservation || { reservedAt: reserveDate, suiteNumber: product.reservedBySuite }
        });

        // Cancel reservation and restore available stock
        const restored: StoreProduct = {
          ...product,
          isReserved: false,
          reservedBySuite: undefined,
          reservedByUserId: undefined,
          reservedAt: undefined,
          reservationStatus: 'disponivel',
          reservation: undefined
        };
        return restored;
      }
    }
    return product;
  });

  return { updatedProducts, hasChanges, cancelledReservations };
}

/**
 * Unified representation of any historical / archived order
 * (Personal Shopper, Store Order/Reservation, Yahoo Auction Bid)
 */
export interface HistoryOrderItem {
  id: string;
  type: 'personal_shopper' | 'store' | 'yahoo_auction';
  typeName: string;
  typeIcon: string;
  title: string;
  originalJapaneseTitle?: string;
  imageUrl?: string;
  storeName: string;
  suiteNumber: string;
  userId: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  priceJpy: number;
  totalJpy?: number;
  totalBrl?: number;
  quantity: number;
  status: string;
  statusBadgeType: 'pending' | 'success' | 'cancelled' | 'processing';
  statusLabel: string;
  createdAt: string;
  updatedAt?: string;
  isOlderThan14Days: boolean;
  isExpiredCancelled?: boolean;
  productUrl?: string;
  notes?: string;
  paymentDetails?: any;
  rawOrder?: any;
}

/**
 * Builds the unified archive list from all collections.
 */
export function buildUnifiedHistoryList(
  personalShopperOrders: PersonalShopperOrder[],
  storeProducts: StoreProduct[],
  yahooBids: YahooAuctionBid[],
  packages: PackageItem[] = []
): HistoryOrderItem[] {
  const history: HistoryOrderItem[] = [];

  // 1. Personal Shopper Orders
  personalShopperOrders.forEach((o) => {
    const dateRef = o.createdAt || o.updatedAt || new Date().toISOString();
    const isCancelledExp = o.status === 'Cancelado' && (o.notes || '').includes('5 dias');
    const isOlder = isOrderOlderThan14Days(dateRef);

    let statusBadgeType: 'pending' | 'success' | 'cancelled' | 'processing' = 'pending';
    let statusLabel: string = o.status;

    if (o.status === 'Cancelado') {
      statusBadgeType = 'cancelled';
      statusLabel = isCancelledExp ? 'Cancelado (Expirou 5 dias)' : 'Cancelado';
    } else if (o.status === 'Recebido na Suíte' || o.status === 'Comprado') {
      statusBadgeType = 'success';
    } else if (o.status === 'Pago - Em Compra') {
      statusBadgeType = 'processing';
    }

    history.push({
      id: o.id,
      type: 'personal_shopper',
      typeName: 'Personal Shopper',
      typeIcon: '🛒',
      title: o.productTitle,
      originalJapaneseTitle: o.originalJapaneseTitle,
      imageUrl: o.productImageUrl,
      storeName: o.storeName || 'Loja JP',
      suiteNumber: o.suiteNumber,
      userId: o.userId,
      clientName: o.clientName,
      clientEmail: o.clientEmail,
      clientPhone: o.clientPhone,
      priceJpy: o.productPriceJpy,
      totalJpy: o.totalJpy,
      totalBrl: o.paymentDetails?.totalAmountBrl,
      quantity: o.quantity || 1,
      status: o.status,
      statusBadgeType,
      statusLabel,
      createdAt: dateRef,
      updatedAt: o.updatedAt,
      isOlderThan14Days: isOlder,
      isExpiredCancelled: isCancelledExp,
      productUrl: o.productUrl,
      notes: o.notes,
      paymentDetails: o.paymentDetails,
      rawOrder: o
    });
  });

  // 2. Store Orders (from Packages delivered from Store + active/past reservations)
  packages
    .filter((pkg) => pkg.store === 'Nossa Loja Do Japão' || (pkg.trackingIn || '').startsWith('LOJA-JP-'))
    .forEach((pkg) => {
      const dateRef = pkg.arrivalDate || pkg.createdAt || new Date().toISOString();
      const isOlder = isOrderOlderThan14Days(dateRef);

      history.push({
        id: pkg.id,
        type: 'store',
        typeName: 'Loja Do Japão',
        typeIcon: '🏪',
        title: pkg.description || 'Produto da Loja Do Japão',
        imageUrl: pkg.photoUrl,
        storeName: 'Nossa Loja Do Japão',
        suiteNumber: pkg.suiteNumber,
        userId: pkg.userId,
        priceJpy: pkg.declaredValue || 0,
        totalJpy: pkg.declaredValue || 0,
        quantity: 1,
        status: 'Pago - Recebido',
        statusBadgeType: 'success',
        statusLabel: 'Pago e Entregue na Suíte',
        createdAt: dateRef,
        isOlderThan14Days: isOlder,
        notes: pkg.description,
        rawOrder: pkg
      });
    });

  // Also include products with current or past reservations
  storeProducts.forEach((p) => {
    if (p.reservation && p.reservation.reservedAt) {
      const dateRef = p.reservation.reservedAt;
      const isOlder = isOrderOlderThan14Days(dateRef);
      const isPaid = p.reservationStatus === 'pago';
      const isReserved = p.reservationStatus === 'reservado';

      history.push({
        id: `store-res-${p.id}-${dateRef.substring(0, 10)}`,
        type: 'store',
        typeName: 'Loja Do Japão (Reserva)',
        typeIcon: '🏪',
        title: p.title,
        imageUrl: p.image,
        storeName: 'Nossa Loja Do Japão',
        suiteNumber: p.reservation.suiteNumber || p.reservedBySuite || 'N/A',
        userId: p.reservation.userId || p.reservedByUserId || '',
        clientName: p.reservation.clientName,
        clientEmail: p.reservation.clientEmail,
        clientPhone: p.reservation.clientPhone,
        priceJpy: p.priceJpy,
        totalJpy: p.reservation.totalJpy || p.priceJpy,
        totalBrl: p.reservation.totalBrl,
        quantity: p.reservation.quantity || 1,
        status: isPaid ? 'Pago' : isReserved ? 'Aguardando Pagamento' : 'Cancelado',
        statusBadgeType: isPaid ? 'success' : isReserved ? 'pending' : 'cancelled',
        statusLabel: isPaid ? 'Pago' : isReserved ? 'Reserva (Aguardando Pagamento)' : 'Cancelado',
        createdAt: dateRef,
        isOlderThan14Days: isOlder,
        notes: p.reservation.notes,
        productUrl: `/loja#product-${p.id}`,
        rawOrder: p
      });
    }
  });

  // Also check stored cancelled reservations in localStorage
  try {
    const rawCancelled = localStorage.getItem('dojapao_cancelled_store_reservations');
    if (rawCancelled) {
      const parsed: any[] = JSON.parse(rawCancelled);
      parsed.forEach((c) => {
        const dateRef = c.reservedAt || c.cancelledAt || new Date().toISOString();
        history.push({
          id: `store-cancel-${c.id || Math.random().toString(36).substring(2, 8)}`,
          type: 'store',
          typeName: 'Loja Do Japão',
          typeIcon: '🏪',
          title: c.productTitle || 'Produto Reservado na Loja',
          imageUrl: c.productImage,
          storeName: 'Nossa Loja Do Japão',
          suiteNumber: c.suiteNumber || 'N/A',
          userId: c.userId || '',
          clientName: c.clientName,
          priceJpy: c.priceJpy || 0,
          totalJpy: c.totalJpy || 0,
          totalBrl: c.totalBrl,
          quantity: c.quantity || 1,
          status: 'Cancelado',
          statusBadgeType: 'cancelled',
          statusLabel: 'Cancelado por Expiração (5 dias)',
          createdAt: dateRef,
          isOlderThan14Days: isOrderOlderThan14Days(dateRef),
          isExpiredCancelled: true,
          notes: 'Cancelado automaticamente por falta de pagamento dentro de 5 dias.'
        });
      });
    }
  } catch {}

  // 3. Yahoo Auction Bids
  yahooBids.forEach((b) => {
    const dateRef = b.createdAt || b.updatedAt || new Date().toISOString();
    const isOlder = isOrderOlderThan14Days(dateRef);

    let statusBadgeType: 'pending' | 'success' | 'cancelled' | 'processing' = 'pending';
    let statusLabel: string = b.status;

    if (b.status === 'arrematado') {
      statusBadgeType = 'success';
      statusLabel = 'Leilão Vencido / Arrematado';
    } else if (b.status === 'retido') {
      statusBadgeType = 'processing';
      statusLabel = 'Saldo Retido (Lance Ativo)';
    } else if (b.status === 'ultrapassado' || b.status === 'cancelado') {
      statusBadgeType = 'cancelled';
      statusLabel = b.status === 'ultrapassado' ? 'Lance Ultrapassado (Estornado)' : 'Cancelado';
    } else if (b.status === 'aguardando_pagamento') {
      statusBadgeType = 'pending';
      statusLabel = 'Aguardando Pagamento';
    }

    history.push({
      id: b.id,
      type: 'yahoo_auction',
      typeName: 'Leilão Yahoo Japan',
      typeIcon: '🔨',
      title: b.auctionTitle,
      imageUrl: b.auctionImageUrl,
      storeName: 'Yahoo! Auctions JP',
      suiteNumber: b.suiteNumber,
      userId: b.userId,
      clientName: b.clientName,
      clientEmail: b.clientEmail,
      priceJpy: b.maxBidJpy,
      totalJpy: b.totalJpy,
      totalBrl: b.heldAmountBrl,
      quantity: 1,
      status: b.status,
      statusBadgeType,
      statusLabel,
      createdAt: dateRef,
      updatedAt: b.updatedAt,
      isOlderThan14Days: isOlder,
      productUrl: b.auctionUrl,
      notes: b.notes,
      paymentDetails: b.paymentDetails,
      rawOrder: b
    });
  });

  // Sort descending by date
  history.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return history;
}

/**
 * Helper to record cancelled store reservations for the history archive.
 */
export function recordCancelledStoreReservation(product: StoreProduct, res: any) {
  try {
    const raw = localStorage.getItem('dojapao_cancelled_store_reservations');
    const list = raw ? JSON.parse(raw) : [];
    const item = {
      id: `cancel-${Date.now()}`,
      productId: product.id,
      productTitle: product.title,
      productImage: product.image,
      suiteNumber: res?.suiteNumber || product.reservedBySuite || 'N/A',
      userId: res?.userId || product.reservedByUserId || '',
      clientName: res?.clientName || '',
      priceJpy: product.priceJpy,
      totalJpy: res?.totalJpy || product.priceJpy,
      totalBrl: res?.totalBrl,
      quantity: res?.quantity || 1,
      reservedAt: res?.reservedAt || new Date().toISOString(),
      cancelledAt: new Date().toISOString(),
      reason: 'Cancelado automaticamente por falta de pagamento (prazo de 5 dias expirado).'
    };
    list.unshift(item);
    localStorage.setItem('dojapao_cancelled_store_reservations', JSON.stringify(list.slice(0, 100)));
  } catch {}
}
